import { Link } from "react-router-dom";
import { z } from "zod";
import { Field, useForm, SuccessPanel, SubmitRow, Honeypot, Consent, consentRule } from "@/components/forms/FormKit";

export const SERVICE_OPTIONS = ["MVP development", "AI engineering", "Fractional CTO", "Architecture and cloud", "Product modernization", "Product discovery", "Venture partnership", "Other"];

const schema = z.object({
  name: z.string().trim().min(2, "Please enter your name."),
  email: z.string().trim().email("Please enter a valid work email."),
  company: z.string().max(160).optional(),
  service: z.enum(SERVICE_OPTIONS, { errorMap: () => ({ message: "Please choose what you need help with." }) }),
  description: z.string().trim().min(20, "Please share a little more (at least 20 characters)."),
  stage_budget: z.string().optional(),
  preferred_contact: z.string().optional(),
  consent: consentRule,
});

export default function ContactForm({ defaultService = "" }) {
  const f = useForm({
    initial: { name: "", email: "", company: "", service: defaultService, description: "", stage_budget: "", preferred_contact: "", consent: false, website: "" },
    schema, endpoint: "/contact", kind: "contact_form",
  });
  if (f.state === "success") {
    return <SuccessPanel testId="contact-success" title="Thanks — we've received your message." text="Your inquiry is safely with us. We'll review it and reply to the email you provided, usually within a few business days." />;
  }
  return (
    <form onSubmit={f.submit} noValidate className="relative space-y-6" data-testid="contact-form">
      <Honeypot bind={f.bind} />
      <div className="grid gap-6 sm:grid-cols-2">
        <Field label="Name" name="name" error={f.errors.name}><input className="field" autoComplete="name" {...f.bind("name")} /></Field>
        <Field label="Work email" name="email" error={f.errors.email}><input type="email" className="field" autoComplete="email" {...f.bind("email")} /></Field>
      </div>
      <div className="grid gap-6 sm:grid-cols-2">
        <Field label="Company or product" name="company" optional><input className="field" autoComplete="organization" {...f.bind("company")} /></Field>
        <Field label="What do you need help with?" name="service" error={f.errors.service}>
          <select className="field appearance-none" {...f.bind("service")}>
            <option value="" disabled>Select one</option>
            {SERVICE_OPTIONS.map((s) => <option key={s} value={s} className="bg-si-bg">{s}</option>)}
          </select>
        </Field>
      </div>
      <Field label="Project description" name="description" error={f.errors.description} hint="What are you building, where are you stuck, and what would a good outcome look like?">
        <textarea rows={6} className="field resize-y" {...f.bind("description")} />
      </Field>
      <div className="grid gap-6 sm:grid-cols-2">
        <Field label="Approximate stage or budget range" name="stage_budget" optional>
          <input className="field" placeholder="e.g. idea stage, pre-seed, existing product" {...f.bind("stage_budget")} />
        </Field>
        <Field label="Preferred contact method" name="preferred_contact" optional>
          <select className="field appearance-none" {...f.bind("preferred_contact")}>
            <option value="">No preference</option>
            <option value="Email" className="bg-si-bg">Email</option>
            <option value="Video call" className="bg-si-bg">Video call</option>
            <option value="Phone" className="bg-si-bg">Phone</option>
          </select>
        </Field>
      </div>
      <Consent bind={f.bind} error={f.errors.consent}>
        I agree that SparkIdea Interactive may store and use the information in this form to respond to my inquiry, as described in the <Link to="/privacy" className="text-si-cyan underline underline-offset-4">Privacy Policy</Link>.
      </Consent>
      <SubmitRow state={f.state} serverError={f.serverError} label="Send Message" testId="contact-submit" />
    </form>
  );
}
